import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import s from './choose-date.module.scss';
import classNames from 'classnames';
import { useGroupedTimeSlots } from '@features/schedule/api/useGroupedTimeSlots';
import { queryClient } from '@shared/api/query-client';
import { useSchedule } from '@widgets/student/api/useSchedule';
import { TextWithEmoji } from '@shared/ui/text-with-emoji/text-with-emoji';
import { Loader } from '@shared/ui/loader/loader';
import { IconSquare, IconSquareCheckFilled, IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { addDays, addHours, startOfDay, isAfter, format, startOfWeek, addMinutes, parse } from 'date-fns';
import { ru } from 'date-fns/locale';
import { Tooltip } from 'react-tooltip';
import { TRIAL_LESSON_DURATION, isTrialAvailable } from '@shared/lib/trial';

const RU_SHORT_WEEKDAY = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

const parseSelectedDate = (dateString) => {
	if (!dateString) return null;
	const parsed = parse(dateString, 'dd.MM.yyyy', new Date());
	if (Number.isNaN(parsed.getTime())) return null;
	return parsed;
};

const normalizeTime = (time) => {
	if (!time || typeof time !== 'string') return null;
	const [h, m] = time.split(':');
	if (h == null || m == null) return null;
	return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
};

const isValidTimeString = (time) => {
	const normalized = normalizeTime(time);
	if (!normalized) return false;
	const [h, m] = normalized.split(':').map(Number);
	return h >= 0 && h < 24 && m >= 0 && m < 60;
};

export const ChooseDate = ({ selectedDate, set_selectedDate, teacher_id, selectedTime, set_selectedTime, selectedInstrument, set_selectedInstrument }) => {
	const timesheetRef = useRef(null);
	const [showScrollHint, set_showScrollHint] = useState(false);

	// Запись доступна не раньше чем через 24 часа
	const minBookableDate = useMemo(() => addHours(new Date(), 24), []);
	const minBookableDay = useMemo(() => startOfDay(minBookableDate), [minBookableDate]);

	const teacher = queryClient.getQueryData(['teacher', teacher_id]);
	const lessonDuration = isTrialAvailable(teacher) ? TRIAL_LESSON_DURATION : teacher?.grade?.duration || 50;

	const [weekStart, setWeekStart] = useState(minBookableDay);
	const selectedDateObj = useMemo(() => parseSelectedDate(selectedDate), [selectedDate]);

	useEffect(() => {
		if (!selectedDateObj) return;
		if (selectedDateObj.getTime() < minBookableDay.getTime()) return;
		const start = startOfWeek(selectedDateObj, { weekStartsOn: 1 });
		setWeekStart(start.getTime() < minBookableDay.getTime() ? minBookableDay : start);
	}, [selectedDateObj, minBookableDay]);

	const weekEnd = addDays(weekStart, 6);
	const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

	const localFormat = (date) => format(date, 'dd.MM.yyyy');

	const date_from = localFormat(weekStart);
	const date_to = localFormat(weekEnd);

	const weeklyParams = useMemo(() => ({ date_from, date_to }), [date_from, date_to]);
	const { data: schedule } = useSchedule(teacher_id, weeklyParams);

	const dailyParams = useMemo(
		() =>
			selectedDateObj
				? {
						date_from: localFormat(selectedDateObj),
						date_to: localFormat(selectedDateObj),
				  }
				: null,
		[selectedDateObj],
	);
	const { data: scheduleByDay, isLoading: isDailyScheduleLoading } = useSchedule(teacher_id, dailyParams);
	const validSchedule = useMemo(() => schedule?.filter((el) => isValidTimeString(el.time)) || [], [schedule]);
	const validScheduleByDay = useMemo(() => scheduleByDay?.filter((el) => isValidTimeString(el.time)) || [], [scheduleByDay]);
	const groupedTimeSlots = useGroupedTimeSlots(validSchedule);
	const isDateDisabled = useCallback((day) => {
		const weekday = day.toLocaleDateString('en-us', { weekday: 'long' }).toLowerCase();
		const slots = groupedTimeSlots?.[weekday]?.times || [];
		const hasSlots = slots.length > 0;
		const hasFreeSlots = slots.some((slot) => !slot?.reserved);
		const allBusy = hasSlots && !hasFreeSlots;
		const isPast = day.getTime() < minBookableDay.getTime();
		return { disabled: !hasSlots || isPast || allBusy, isPast, hasSlots, allBusy };
	}, [groupedTimeSlots, minBookableDay]);

	// Слот недоступен, если начинается раньше, чем через 24 часа от текущего момента
	const isSlotTooEarly = useCallback((time) => {
		if (!selectedDateObj || !isValidTimeString(time)) return false;
		const slotStart = parse(normalizeTime(time), 'HH:mm', selectedDateObj);
		return slotStart.getTime() < minBookableDate.getTime();
	}, [selectedDateObj, minBookableDate]);


	const canPrevWeek = isAfter(weekStart, minBookableDay);
	const prevWeek = () => { if (canPrevWeek) setWeekStart(addDays(weekStart, -7)); };
	const nextWeek = () => setWeekStart(addDays(weekStart, 7));

	const weekLabel = `${format(weekStart, 'd MMMM', { locale: ru })} - ${format(weekEnd, 'd MMMM', { locale: ru })}`;

	useEffect(() => {
		const el = timesheetRef.current;
		if (!el) return;

		const checkOverflow = () => {
			const hasOverflow = el.scrollWidth > el.clientWidth + 1;
			const isAtEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
			set_showScrollHint(hasOverflow && !isAtEnd);
		};

		checkOverflow();
		el.addEventListener('scroll', checkOverflow, { passive: true });
		window.addEventListener('resize', checkOverflow);
		return () => {
			el.removeEventListener('scroll', checkOverflow);
			window.removeEventListener('resize', checkOverflow);
		};
	}, [scheduleByDay]);

	const onChooseDate = useCallback((value) => {
		set_selectedDate(value);
		set_selectedTime(null);
		window.ym(96194074, 'reachGoal', 'select-date-by-teacher-page');
	}, [set_selectedDate, set_selectedTime]);

	const onChooseTime = useCallback((value) => {
		set_selectedTime(normalizeTime(value) || value);
		window.ym(96194074, 'reachGoal', 'select-time-by-teacher-page');
	}, [set_selectedTime]);

	const onChooseInstrument = useCallback((value) => {
		set_selectedInstrument(value);
		window.ym(96194074, 'reachGoal', 'select-instrument-by-teacher-page');
	}, [set_selectedInstrument]);

	useEffect(() => {
		if (selectedInstrument) return;
		const firstInstrument = teacher?.instruments?.[0];
		if (firstInstrument) onChooseInstrument(firstInstrument);
	}, [selectedInstrument, teacher?.instruments, onChooseInstrument]);

	useEffect(() => {
		if (selectedDate || !selectedInstrument) return;
		const firstAvailable = weekDays.find((day) => !isDateDisabled(day).disabled);
		if (firstAvailable) onChooseDate(localFormat(firstAvailable));
	}, [selectedDate, selectedInstrument, weekDays, groupedTimeSlots, isDateDisabled, onChooseDate]);

	useEffect(() => {
		if (!selectedDate || selectedTime || isDailyScheduleLoading) return;
		const firstSlot = validScheduleByDay?.find((el) => !el?.reserved && !isSlotTooEarly(el?.time));
		if (firstSlot) onChooseTime(firstSlot.time);
	}, [selectedDate, selectedTime, validScheduleByDay, isDailyScheduleLoading, onChooseTime, isSlotTooEarly]);

	// При предвыборе даты/времени (например, из URL) проверяем, что день и слот действительно свободны
	useEffect(() => {
		if (!selectedDateObj || !Array.isArray(validScheduleByDay) || isDailyScheduleLoading) return;

		if (selectedDateObj.getTime() < minBookableDay.getTime()) {
			set_selectedDate(null);
			set_selectedTime(null);
			return;
		}

		if (!validScheduleByDay.length || validScheduleByDay.every((el) => el?.reserved)) {
			set_selectedDate(null);
			set_selectedTime(null);
			return;
		}

		if (selectedTime) {
			const normalizedSelectedTime = normalizeTime(selectedTime);
			const slot = validScheduleByDay.find((el) => normalizeTime(el.time) === normalizedSelectedTime);
			if (!slot || slot.reserved || isSlotTooEarly(slot.time)) {
				set_selectedTime(null);
			}
		}
	}, [selectedDateObj, selectedTime, validScheduleByDay, minBookableDay, set_selectedDate, set_selectedTime, isDailyScheduleLoading, isSlotTooEarly]);

	const getTimeString = (time) => {
		if (!isValidTimeString(time)) return time || '—';
		try {
			const normalized = normalizeTime(time);
			const start = parse(normalized, 'HH:mm', new Date());
			const end = addMinutes(start, Number(lessonDuration) || 50);
			return `${format(start, 'HH:mm')}–${format(end, 'HH:mm')}`;
		} catch {
			return time || '—';
		}
	};

	return (
		<div className={s.wrapper}>
			<div className={s.section}>
				<h3 className={s.sectionTitle}>Выберите инструмент</h3>
				<div className={s.instrumentGrid}>
					{teacher?.instruments?.map((el) => {
						const active = selectedInstrument?.id === el.id;
						return (
							<button
								key={el.id}
								type='button'
								className={classNames(s.instrumentCard, active && s.instrumentCardActive)}
								onClick={() => onChooseInstrument(el)}
							>
								{active
									? <IconSquareCheckFilled size={24} className={s.checkIcon} />
									: <IconSquare size={24} className={s.squareIcon} />}
								<span className={s.instrumentName}><TextWithEmoji text={el.name} /></span>
							</button>
						);
					})}
				</div>
			</div>

			<div className={classNames(s.section, !selectedInstrument && s.sectionDisabled)}>
				<h3 className={s.sectionTitle}>Выберите дату</h3>

				<div className={s.weekNav}>
					<button type='button' className={s.weekArrow} onClick={prevWeek} disabled={!canPrevWeek} aria-label='Предыдущая неделя'>
						<IconChevronLeft size={20} />
					</button>
					<div className={s.weekLabelWrapper}>
						<span className={s.weekLabel}>{weekLabel}</span>
					</div>
					<button type='button' className={s.weekArrow} onClick={nextWeek} aria-label='Следующая неделя'>
						<IconChevronRight size={20} />
					</button>
				</div>

				<div className={s.daysRow}>
					{weekDays.map((day) => {
						const { disabled, isPast, hasSlots, allBusy } = isDateDisabled(day);
						const dateString = localFormat(day);
						const active = selectedDate === dateString;
						const disabledReason = isPast
							? 'Запись на эту дату недоступна'
							: allBusy
							? 'Все слоты заняты'
							: !hasSlots
							? 'Нет свободного времени'
							: null;

						return (
							<button
								key={dateString}
								type='button'
								className={classNames(s.dayCard, active && s.dayCardActive, disabled && s.dayCardDisabled)}
								disabled={disabled || !selectedInstrument}
								onClick={() => !disabled && selectedInstrument && onChooseDate(dateString)}
								data-tooltip-id={disabledReason ? 'day-disabled-tooltip' : undefined}
								data-tooltip-content={disabledReason || undefined}
							>
								<span className={s.dayNumber}>{day.getDate()}</span>
								<span className={s.dayWeekday}>{RU_SHORT_WEEKDAY[day.getDay()]}</span>
							</button>
						);
					})}
				</div>
			</div>

			<div className={classNames(s.section, !selectedDate && s.sectionDisabled)}>
				<h3 className={s.sectionTitle}>Выберите время</h3>

				{selectedDate && isDailyScheduleLoading && (
					<div className={s.loaderWrapper}>
						<Loader />
					</div>
				)}

				{selectedDate && !isDailyScheduleLoading && !validScheduleByDay?.length && (
					<div className={s.emptySlots}>
						На выбранную дату нет свободных окон. Выберите другой день.
					</div>
				)}

				{selectedDate && !isDailyScheduleLoading && validScheduleByDay?.length > 0 && (
					<div
						ref={timesheetRef}
						className={classNames(s.timesheet, showScrollHint && s.timesheetWithHint)}
					>
						{validScheduleByDay.map((el) => {
							const isDisabled = el?.reserved || isSlotTooEarly(el?.time);
							const isActive = selectedTime === el?.time;

							return (
								<button
									key={el?.time}
									type='button'
									className={classNames(s.timeSlot, isActive && s.timeSlotActive, isDisabled && s.timeSlotDisabled)}
									disabled={isDisabled}
									onClick={() => !isDisabled && onChooseTime(el?.time)}
								>
									{getTimeString(el?.time)}
								</button>
							);
						})}
					</div>
				)}
			</div>

			<Tooltip id='day-disabled-tooltip' place='top' />
		</div>
	);
};
